import { xor } from 'lodash';
import React, {
    MutableRefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import type uPlot from 'uplot';

import WaitSpinner from '@splunk/react-ui/WaitSpinner';
import { SplunkThemeProvider } from '@splunk/themes';
import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';

import { LocaleInfo } from './utilities/formatTimestamp';
import { AlertPeriod, AlertBarConfig, AlertBarContainer } from './AlertBar';
import {
    EventAnnotation,
    EventAnnotationConfig,
    EventAnnotationContainer,
    EventAnnotationWResolution,
} from './EventAnnotations';
import { Legend, SideLegend } from './Legends';
import { MarksContainer } from './Marks';
import { PinContainer, PinValue } from './Pin';
import { RangeSelection } from './RangeSelection';
import {
    StyledTimeSeriesChart,
    StyledTimeseriesChartContainer,
    StyledTooltipOutsideChartWrapper,
    tooltipOutsideChartContainerHeightRaw,
} from './TimeSeriesChart.styled';
import { Tooltip, TooltipContainer, TooltipOutsideChart } from './Tooltips';
import { useCursorPlugin, CursorState } from './cursorPlugin';
import {
    Range,
    RangeSelectionProviderContextOutput,
    useRangeSelection,
} from './providers/RangeSelectionProvider';
import { VizContextOutputType } from './providers/VizContextProvider';
import { useVizContext } from './providers/useVizContext';
import type { PlotUtilitiesFn, PlotUtilities, AxisDisplayProps, AreaOnlyOptions } from './renderers/types';
import { useUplotPlotRendererFactory } from './renderers/uplot';
import { useVizTheme, VizThemeKey } from './theme';
import type {
    AdditionalTooltipValues,
    AxisRange,
    DataSet,
    GenericDataSet,
    LegendTruncation,
    MarkAnnotation,
    ScaleConfig,
    SeriesConfig,
    TimestampFormat,
} from './types';
import { useDimensions } from './useDimensions';
import {
    DAY,
    EPOCH_SECONDS_MAX_VALUE,
    exists,
    filterAdditionalTooltipValues,
    getXAxisResolution,
    markPrefix,
    normalizeTimeValue,
    normalizeAndFilterTimeSeriesColumns,
    sortDataSetByTime,
    useCreateAttachedDivElement,
    useDynamicCallback,
} from './utilities/utils';
import { isTimeSeriesData, getLocaleInfo, getIsSingleVizFullscreen } from './renderers/uplot-utils';
import { useScaleFactor } from './utilities/useScaleFactor';

/** Global counter to get a unique id for each chart instance. */
let globalId = 0;

export type CrosshairValue = {
    // unscaled time
    time: number;
    seriesKey?: string;
    index?: number;
    seriesIndex?: number;
    value?: number;
    rowContext?: Record<string, string | number | null>;
};

export type CrosshairClickEvent = {
    time: number;
    index: number;
    seriesIndex: number;
    originalEvent: MouseEvent;
    seriesKey: string;
    value?: number;
    rowContext?: Record<string, string | number | null>;
};

export type CrosshairClickEventWResolution = CrosshairClickEvent & {
    xResolution: number;
};

export type PlotDimensions = {
    root: DOMRect;
    over: DOMRect;
};

type RowContext = Record<string, string | number | null>;

export type InternalTimeSeriesChartProps = AxisDisplayProps &
    AreaOnlyOptions & {
        id?: string;

        // Dimensions
        width: number | string;
        height: number | string;

        loading?: boolean;

        // Raw data
        data: GenericDataSet;

        // Series configuration
        seriesConfig?: SeriesConfig;
        // Scale/Axis configuration
        scaleConfig?: ScaleConfig;

        // hide x-axis flag
        hideXAxis?: true;
        // hide tooltip series label header
        hideTooltipSeriesLabel?: boolean;
        // Configuration to use for legend; false to disable legends
        legend?: false | Legend;
        // Component to use for tooltip; false to disable tooltips
        tooltip?: false | Tooltip;
        /**
         * Display tooltip values outside chart instead of displaying on chart.
         * If set to true, tooltip prop is overridden.
         */
        showTooltipOutsideChart?: boolean;

        onSeriesClick?: (field: string, anchorRef: React.Ref<HTMLElement>) => void;

        // Alerts
        alerts?: AlertPeriod[];
        alertsConfig?: AlertBarConfig;

        // Events
        events?: EventAnnotation[];
        eventsConfig?: EventAnnotationConfig;

        // Marks
        marks?: MarkAnnotation[];

        /**
         * Callback that triggers when the chart is initialized.
         *
         * The is a workaround for 1 specific team, olly, and should not be relied on.
         */
        onReady?: (utils?: PlotUtilitiesFn, uplotRef?: uPlot) => void;

        // Cursor
        onCrosshairChange?: (crosshairValue?: CrosshairValue) => void;
        onCrosshairClick?: (event: CrosshairClickEventWResolution) => void;

        // Pin
        onPinClose?: () => void;

        // Range selection
        onRangeSelection?: (range: Range | undefined, e: MouseEvent) => void;
        onRangeZoom?: (range: Range, e: MouseEvent) => void;
        serverSideZoom?: boolean; // If true, zoom triggers onRangeZoom callback; otherwise performs client-side zoom
        title?: string;

        // Displaying chart based on user locale preferences
        userLocale?: LocaleInfo;

        // Range
        range?: AxisRange;

        /** Toggles the visibility of a given series when it is clicked in the legend.
         * Note: if this is `true` `onSeriesClick` will not be called */
        legendClickTogglesSeriesVisibility?: boolean;

        /** Maximum number of series that still allows for "closest series focus" functionality to be enabled.
         * defaults to FOCUS_MAX_SERIES (30)
         */
        maxSeriesFocus?: number;

        /** X-axis timeseries label format - compact(one-line), expanded (multi-line)
         * defaults to compact
         */
        timestampFormat?: TimestampFormat;

        /** Color palette used for series coloring
         * defaults to VIZ_CATEGORICAL color palette
         */
        seriesColors?: string[];

        /** Specify a color for a field in the series
         * defaults to seriesColors if this option is not set
         */
        seriesColorsByField?: Record<string, string>;

        /**
         * Additional values to display in the tooltip.
         * Array of objects, one per data point, mapping field names to values.
         */
        additionalTooltipValues?: AdditionalTooltipValues;

        /**
         * Ref to additional tooltip values. Used to avoid chart remounts when values change.
         * Takes precedence over additionalTooltipValues if provided.
         */
        additionalTooltipValuesRef?: React.MutableRefObject<AdditionalTooltipValues | undefined>;

        /** Add additional uplot plugins */
        plugins?: uPlot.Plugin[];

        /** Specify a list of labels to pre-populate the legend */
        legendLabels?: string[];
        /** Whether we're in series compare mode (affects legend behavior) */
        isSeriesCompare?: boolean;
        /** Controls how legend labels are truncated when they exceed available space */
        legendTruncation?: LegendTruncation;
    };

type InteractionOrigin = {
    selection: boolean;
    hover: boolean;
    range: boolean;
};

const useSharedInteractions = (
    vizId: string,
    loading?: boolean
): VizContextOutputType &
    RangeSelectionProviderContextOutput & {
        isInteractionOrigin: MutableRefObject<InteractionOrigin>;
    } => {
    const isInteractionOrigin = useRef<InteractionOrigin>({
        selection: false,
        hover: false,
        range: false,
    });
    const {
        syncedHoverValue,
        syncedSelectedValue,
        setSyncedSelectedValue,
        setSyncedHoverValue,
        ...restVizContext
    } = useVizContext();

    const rangeSelectionOutput = useRangeSelection();

    isInteractionOrigin.current = {
        selection: syncedSelectedValue?.originViz === vizId,
        hover: syncedHoverValue?.originViz === vizId,
        range: rangeSelectionOutput?.rangeSelection?.vizId === vizId,
    };

    useEffect(() => {
        if (rangeSelectionOutput !== undefined) {
            isInteractionOrigin.current.range = rangeSelectionOutput.rangeSelection?.vizId === vizId;
        } else {
            isInteractionOrigin.current.range = false;
        }
    }, [rangeSelectionOutput, isInteractionOrigin, vizId]);

    const setRangeSelection = rangeSelectionOutput?.setRangeSelection;

    const clearSelectionsFromCurrent = useCallback(() => {
        if (isInteractionOrigin.current.selection) {
            setSyncedSelectedValue(undefined);
        }

        if (isInteractionOrigin.current.hover) {
            setSyncedHoverValue(undefined);
        }
        if (isInteractionOrigin.current.range && setRangeSelection) {
            setRangeSelection(undefined);
        }
    }, [isInteractionOrigin, setSyncedSelectedValue, setSyncedHoverValue, setRangeSelection]);

    useEffect(() => {
        if (loading) {
            clearSelectionsFromCurrent();
        }
    }, [clearSelectionsFromCurrent, loading]);

    useEffect(
        () => (): void => {
            clearSelectionsFromCurrent();
        },
        // On unmount, clear shared state
        [clearSelectionsFromCurrent]
    );

    return {
        ...restVizContext,
        syncedHoverValue,
        syncedSelectedValue,
        setSyncedSelectedValue,
        setSyncedHoverValue,
        isInteractionOrigin,
        rangeSelection: rangeSelectionOutput?.rangeSelection,
        setRangeSelection: rangeSelectionOutput?.setRangeSelection,
        isZoomed: rangeSelectionOutput?.isZoomed ?? false,
        setIsZoomed: rangeSelectionOutput?.setIsZoomed ?? (() => {}),
    };
};

const WaitSpinnerWrapper: React.FC<{ children: React.ReactNode }> = props => (
    <div
        style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}
    >
        {props.children}
    </div>
);

const defaultTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

const DEFAULT_LOCALE = {
    tz: defaultTz,
    twelveHourTimeFormat: true,
};

const calculatePlotDimensions = (opts: {
    showTooltipOutsideChart: boolean;
    legend: React.ReactElement | null;
    legendAnchor: string | undefined;
    legendSize: { width?: number; height?: number } | undefined;
    width: number;
    height: number;
    scaleFactor: number;
    actualLegendWidth?: number;
}) => {
    const {
        showTooltipOutsideChart,
        legend,
        legendAnchor,
        legendSize,
        width,
        height,
        scaleFactor,
        actualLegendWidth,
    } = opts;
    const tooltipOutsideChartHeight = showTooltipOutsideChart ? tooltipOutsideChartContainerHeightRaw : 0;
    // Use actual measured legend width if available, otherwise fall back to legendSize
    const effectiveLegendWidth = actualLegendWidth ?? legendSize?.width ?? 0;
    const plotRendererWidth =
        legend && (legendAnchor === 'right' || legendAnchor === 'left')
            ? width - effectiveLegendWidth * scaleFactor
            : width;
    const plotRendererHeight =
        legend && (legendAnchor === 'bottom' || legendAnchor === 'top')
            ? height - (legendSize?.height ?? 0) * scaleFactor - tooltipOutsideChartHeight
            : height - tooltipOutsideChartHeight;

    return {
        scaledPlotRendererWidth: plotRendererWidth / scaleFactor,
        scaledPlotRendererHeight: plotRendererHeight / scaleFactor,
        tooltipOutsideChartHeight,
    };
};

interface BasePortalProps {
    plotUtilities: PlotUtilities;
    cursorState: CursorState | undefined;
}

interface TooltipPortalProps extends BasePortalProps {
    tooltipEl: HTMLElement | null;
    tooltip: InternalTimeSeriesChartProps['tooltip'];
    data: GenericDataSet;
    seriesConfig: InternalTimeSeriesChartProps['seriesConfig'];
    scaleConfig: InternalTimeSeriesChartProps['scaleConfig'];
    eventAnnotationCursorState: EventAnnotation | undefined;
    eventsConfig: InternalTimeSeriesChartProps['eventsConfig'];
    chartTitle: string | undefined;
    dailyAxisResolution: number | undefined;
    localeInfo: LocaleInfo;
    hideTooltipSeriesLabel: boolean | undefined;
    showTooltipOutsideChart: boolean | undefined;
    isTimeSeries: boolean;
    rawXaxisValues: (string | number)[];
    additionalTooltipValues: AdditionalTooltipValues | undefined;
}

interface MarkPortalProps extends BasePortalProps {
    markEl: HTMLElement | null;
    marks: MarkAnnotation[] | undefined;
    selectedField: string | null;
}

interface RangePortalProps extends BasePortalProps {
    rangeEl: HTMLElement | null;
    onRangeSelection: InternalTimeSeriesChartProps['onRangeSelection'];
    rangeSelection: Range | undefined;
    id: string;
    handleRangeSelectionChange: (range: Range | undefined, e: MouseEvent) => void;
    onRangeZoom: ((range: Range, e: MouseEvent) => void) | undefined;
    serverSideZoom?: boolean;
}

interface EventPortalProps extends BasePortalProps {
    eventAnnotationEl: HTMLElement | null;
    events: EventAnnotation[] | undefined;
    eventsConfig: InternalTimeSeriesChartProps['eventsConfig'];
    onEventAnnotationClick:
        | ((
              event: EventAnnotationWResolution,
              mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>
          ) => void)
        | undefined;
    xResolution: number;
    setEventAnnotationCursorState: React.Dispatch<React.SetStateAction<EventAnnotation | undefined>>;
}

interface PinPortalProps extends BasePortalProps {
    pinEl: HTMLElement | null;
    data: GenericDataSet;
    pinValue: PinValue | undefined;
    onPinClose: (() => void) | undefined;
}

interface AlertBarPortalProps extends BasePortalProps {
    alertBarEl: HTMLElement | null;
    alerts: AlertPeriod[] | undefined;
    alertsConfig: InternalTimeSeriesChartProps['alertsConfig'];
    scaledPlotRendererWidth: number;
}

const TooltipPortal: React.FC<TooltipPortalProps> = ({ tooltipEl, cursorState, ...props }) =>
    tooltipEl && cursorState
        ? ReactDOM.createPortal(
              <TooltipContainer
                  plotUtilities={props.plotUtilities}
                  tooltip={props.tooltip}
                  cursorState={cursorState}
                  data={props.data}
                  seriesConfig={props.seriesConfig}
                  scaleConfig={props.scaleConfig}
                  eventAnnotationCursorState={props.eventAnnotationCursorState}
                  eventAnnotationConfig={props.eventsConfig}
                  eventAnnotationTooltip={props.eventsConfig?.tooltip}
                  chartTitle={props.chartTitle}
                  xAxisResolution={props.dailyAxisResolution}
                  userLocale={props.localeInfo}
                  hideTooltipSeriesLabel={props.hideTooltipSeriesLabel}
                  showTooltipOutsideChart={props.showTooltipOutsideChart}
                  isTimeSeries={props.isTimeSeries}
                  rawXaxisValues={props.rawXaxisValues}
                  additionalTooltipValues={props.additionalTooltipValues}
              />,
              tooltipEl
          )
        : null;

const MarkPortal: React.FC<MarkPortalProps> = ({ markEl, selectedField, ...props }) =>
    markEl
        ? ReactDOM.createPortal(
              <MarksContainer
                  plotUtilities={props.plotUtilities}
                  marks={props.marks}
                  selectedMark={selectedField?.startsWith(markPrefix) ? selectedField : undefined}
              />,
              markEl
          )
        : null;

const RangePortal: React.FC<RangePortalProps> = ({
    rangeEl,
    onRangeSelection,
    rangeSelection,
    id,
    handleRangeSelectionChange,
    onRangeZoom,
    serverSideZoom,
    ...props
}) =>
    rangeEl && onRangeSelection
        ? ReactDOM.createPortal(
              <RangeSelection
                  plotUtilities={props.plotUtilities}
                  cursorState={props.cursorState}
                  rangeSelection={rangeSelection?.vizId === id ? rangeSelection : undefined}
                  onRangeDeselection={(e): void => handleRangeSelectionChange(undefined, e)}
                  onRangeZoom={onRangeZoom}
                  serverSideZoom={serverSideZoom}
              />,
              rangeEl
          )
        : null;

const EventPortal: React.FC<EventPortalProps> = ({
    eventAnnotationEl,
    eventsConfig,
    setEventAnnotationCursorState,
    ...props
}) =>
    eventAnnotationEl
        ? ReactDOM.createPortal(
              <EventAnnotationContainer
                  events={props.events}
                  eventAnnotationConfig={eventsConfig}
                  plotUtilities={props.plotUtilities}
                  onEventAnnotationHover={(event): void => {
                      eventsConfig?.onEventAnnotationHover?.(event?.time);
                      setEventAnnotationCursorState(event);
                  }}
                  onEventAnnotationClick={props.onEventAnnotationClick}
                  xResolution={props.xResolution}
              />,
              eventAnnotationEl
          )
        : null;

const PinPortal: React.FC<PinPortalProps> = ({ pinEl, ...props }) =>
    pinEl
        ? ReactDOM.createPortal(
              <PinContainer
                  plotUtilities={props.plotUtilities}
                  data={props.data}
                  pinValue={props.pinValue}
                  onPinClose={props.onPinClose}
              />,
              pinEl
          )
        : null;

const AlertBarPortal: React.FC<AlertBarPortalProps> = ({
    alertBarEl,
    alerts,
    alertsConfig,
    scaledPlotRendererWidth,
    ...props
}) =>
    alertBarEl && alerts
        ? ReactDOM.createPortal(
              <AlertBarContainer
                  periods={alerts}
                  startTime={alerts.length > 0 ? Math.min(...alerts.map(p => p.startTime)) : 0}
                  endTime={alerts.length > 0 ? Math.max(...alerts.map(p => p.endTime)) : 0}
                  config={alertsConfig}
                  width={scaledPlotRendererWidth}
                  plotUtilities={props.plotUtilities}
              />,
              alertBarEl
          )
        : null;

interface ChartPortalsProps {
    tooltipProps: TooltipPortalProps;
    markProps: MarkPortalProps;
    rangeProps: RangePortalProps;
    eventProps: EventPortalProps;
    pinProps: PinPortalProps;
    alertBarProps: AlertBarPortalProps;
}

const ChartPortals: React.FC<ChartPortalsProps> = ({
    tooltipProps,
    markProps,
    rangeProps,
    eventProps,
    pinProps,
    alertBarProps,
}) => (
    <>
        <TooltipPortal {...tooltipProps} />
        <MarkPortal {...markProps} />
        <RangePortal {...rangeProps} />
        <EventPortal {...eventProps} />
        <PinPortal {...pinProps} />
        <AlertBarPortal {...alertBarProps} />
    </>
);

const useLegendWidth = (
    legend: React.ReactElement | null,
    legendAnchor: string | undefined
): { legendRef: React.RefObject<HTMLDivElement>; actualLegendWidth: number | undefined } => {
    const [actualLegendWidth, setActualLegendWidth] = useState<number | undefined>(undefined);
    const legendRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    // SCP-80914: measure inside useLayoutEffect (not a callback ref) so the setState
    // is flushed synchronously before the browser paints. Callback-ref setStates are
    // subject to React 18 automatic batching and can produce an intermediate paint
    // where actualLegendWidth is still undefined, causing plotRendererWidth to snap
    // from the factory fallback to the measured width on the next frame.
    useLayoutEffect(() => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        const el = legendRef.current;
        if (!el || (legendAnchor !== 'right' && legendAnchor !== 'left')) {
            return undefined;
        }

        setActualLegendWidth(el.offsetWidth);

        const observer = new ResizeObserver(entries => {
            const newWidth = entries[0]?.contentRect.width;
            if (newWidth !== undefined) {
                setActualLegendWidth(prev => (prev === undefined || newWidth > prev ? newWidth : prev));
            }
        });
        observer.observe(el);
        observerRef.current = observer;

        return () => observer.disconnect();
    }, [legendAnchor]);

    return { legendRef, actualLegendWidth };
};

const useHandleSelectedStateChange = (
    id: string,
    data: GenericDataSet,
    xResolution: number,
    setSyncedSelectedValue: ReturnType<typeof useSharedInteractions>['setSyncedSelectedValue'],
    setRangeSelection: ReturnType<typeof useSharedInteractions>['setRangeSelection'],
    onCrosshairClickFromProps: InternalTimeSeriesChartProps['onCrosshairClick'],
    getDataOnClick: (si: number | undefined, di: number | undefined, ds: DataSet) => number
) =>
    useCallback(
        /* istanbul ignore next */
        (event?: CrosshairClickEvent): void => {
            if (!event?.time || !event?.seriesIndex || !event?.seriesKey) return;
            setSyncedSelectedValue({ originViz: id, xVal: event.time, seriesKey: event.seriesKey });
            setRangeSelection?.(undefined);
            if (!onCrosshairClickFromProps) return;
            const value = getDataOnClick(event.seriesIndex, event.index, data as DataSet);
            let rowContext: Record<string, string | number | null> | undefined;
            if (event.index !== undefined && data?.columns && data?.fields) {
                rowContext = {};
                data.columns.forEach((column, colIndex) => {
                    if (column?.[event.index] !== undefined) {
                        const fieldName = data.fields[colIndex];
                        if (fieldName) rowContext[fieldName] = column[event.index];
                    }
                });
            }
            onCrosshairClickFromProps({ ...event, xResolution, value, rowContext });
        },
        [
            id,
            data,
            xResolution,
            setSyncedSelectedValue,
            setRangeSelection,
            onCrosshairClickFromProps,
            getDataOnClick,
        ]
    );

const useHandleHoverStateChange = (
    id: string,
    data: GenericDataSet,
    hasProvider: boolean,
    setSyncedHoverValue: ReturnType<typeof useSharedInteractions>['setSyncedHoverValue'],
    onCrosshairChangeFromProps: InternalTimeSeriesChartProps['onCrosshairChange']
) =>
    useCallback(
        /* istanbul ignore next */
        (crosshairValue?: CrosshairValue): void => {
            if (hasProvider) {
                setSyncedHoverValue(
                    crosshairValue?.time
                        ? { originViz: id, xVal: crosshairValue.time, seriesKey: crosshairValue.seriesKey }
                        : undefined
                );
            }
            if (!onCrosshairChangeFromProps) return;
            if (!crosshairValue) {
                onCrosshairChangeFromProps(crosshairValue);
                return;
            }
            const enhancedValue: CrosshairValue = { ...crosshairValue };
            if (
                crosshairValue.seriesIndex !== undefined &&
                crosshairValue.index !== undefined &&
                data?.columns &&
                data?.fields
            ) {
                const seriesData = data.columns[crosshairValue.seriesIndex];
                if (seriesData?.[crosshairValue.index] !== undefined) {
                    enhancedValue.value = seriesData[crosshairValue.index] as number;
                }
                const rowContext: RowContext = {};
                data.columns.forEach((column, colIndex) => {
                    if (column?.[crosshairValue.index] !== undefined) {
                        const fieldName = data.fields[colIndex];
                        if (fieldName) rowContext[fieldName] = column[crosshairValue.index];
                    }
                });
                enhancedValue.rowContext = rowContext;
            }
            onCrosshairChangeFromProps(enhancedValue);
        },
        [id, data, hasProvider, setSyncedHoverValue, onCrosshairChangeFromProps]
    );

const InternalTimeSeriesChart: React.FC<InternalTimeSeriesChartProps> = props => {
    const theme = useVizTheme();

    // Globally unique id for chart
    // eslint-disable-next-line no-plusplus
    const [id] = useState<string>(() => {
        globalId += 1;
        return `${props.id || globalId}`;
    });

    // Pass default locale
    const localeInfo = getLocaleInfo(props?.userLocale, DEFAULT_LOCALE);

    // Create renderer
    const { utils: plotUtilitiesFn, renderer: plotRenderer } = useUplotPlotRendererFactory(props?.onReady);
    const plotUtilities = plotUtilitiesFn();
    const dailyAxisResolution = props.range?.dailyResolution ? DAY : undefined;
    const timestampFormat: TimestampFormat = props?.timestampFormat || 'compact';
    const seriesColors: string[] = props?.seriesColors || VIZ_CATEGORICAL;
    const seriesColorsByField = props?.seriesColorsByField;

    // Retrieve or create interactive element to hold pins. This layer sits on _top_ of the "u-over" element.
    const cursorEl = useCreateAttachedDivElement(plotUtilities?.el, 'olly-viz-cursor');
    const interactiveEl = useCreateAttachedDivElement(plotUtilities?.el, 'olly-viz-interactive');
    const markEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-marks');
    const alertBarEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-alertbar');
    const eventAnnotationEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-eventAnnotations');
    const pinEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-pin');
    const rangeEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-range', 'viz-range-selection');
    const tooltipEl = useCreateAttachedDivElement(interactiveEl, 'olly-viz-tooltip');

    const scaleFactor = useScaleFactor();

    // Basic fullscreen detection
    const isSingleVizFullscreen = getIsSingleVizFullscreen();

    // Dimensions
    const { width: containerWidth, height: containerHeight } = props;
    const {
        ref: containerRef,
        dimensions: { width, height },
    } = useDimensions();

    // Cursor
    const {
        syncedHoverValue,
        setSyncedHoverValue,
        syncedSelectedValue,
        setSyncedSelectedValue,
        rangeSelection,
        setRangeSelection,
        hasProvider,
    } = useSharedInteractions(id, props.loading);

    const {
        onRangeSelection: unstableOnRangeSelection,
        onRangeZoom: unstableOnRangeZoom,
        hideTooltipSeriesLabel,
    } = props;
    const onRangeZoom = useDynamicCallback(unstableOnRangeZoom);

    const handleRangeSelectionChange = useCallback(
        (range: Range | undefined, e: MouseEvent): void => {
            if (setRangeSelection) {
                setRangeSelection(range);
            }
            if (setSyncedSelectedValue) {
                setSyncedSelectedValue(undefined);
            }
            if (unstableOnRangeSelection) {
                unstableOnRangeSelection(range, e);
            }
        },
        [unstableOnRangeSelection, setRangeSelection, setSyncedSelectedValue]
    );
    const onRangeSelection = useCallback(
        (range: Range) => handleRangeSelectionChange(range, new MouseEvent('click')),
        [handleRangeSelectionChange]
    );

    const {
        data: rawData,
        onCrosshairClick: onCrosshairClickFromProps,
        onCrosshairChange: onCrosshairChangeFromProps,
    } = props;

    const isTimeSeries = isTimeSeriesData(rawData);
    const rawXaxisValues = props.data.columns[0];
    const [data, timeWindow, sortedIndices, filteredIndices]: [
        GenericDataSet,
        [number, number] | undefined,
        number[] | null | undefined,
        number[] | null | undefined,
    ] = useMemo(() => {
        // normalize the incoming data to support time values as epoch seconds, epoch milliseconds, or ISO strings

        const normalizedData: GenericDataSet = {
            fields: rawData.fields,
            columns: rawData.columns.slice(0) as [(string | number)[], ...number[][]],
        };
        let dataPoints = normalizedData.columns[0];

        if (!dataPoints || dataPoints.length === 0) {
            return [normalizedData, undefined, null, null];
        }
        if (
            isTimeSeries &&
            (typeof dataPoints[0] === 'string' ||
                (typeof dataPoints[0] === 'number' && dataPoints[0] > EPOCH_SECONDS_MAX_VALUE))
        ) {
            normalizedData.columns[0] = dataPoints.map(normalizeTimeValue) as (string | number)[] | number[];
        }
        // If categorical x-axis (non time stamps), then pass only the index values for column[0]
        if (!isTimeSeries) {
            // if categorical data
            normalizedData.columns[0] = dataPoints.map((v, idx) => idx);
        }

        if (isTimeSeries) {
            const { filteredData, filteredIndices: fIndices } =
                normalizeAndFilterTimeSeriesColumns(normalizedData);
            const { sortedData, sortedIndices: indices } = sortDataSetByTime(
                filteredData as unknown as DataSet
            );
            dataPoints = sortedData.columns[0]; // eslint-disable-line prefer-destructuring
            return [
                sortedData as unknown as GenericDataSet,
                [Number(dataPoints[0]) || -1, Number(dataPoints.at(-1)) || -1],
                indices,
                fIndices,
            ];
        }

        dataPoints = normalizedData.columns[0]; // eslint-disable-line prefer-destructuring
        return [
            normalizedData as unknown as GenericDataSet,
            [Number(dataPoints[0]) || -1, Number(dataPoints.at(-1)) || -1],
            null,
            null,
        ];
    }, [rawData, isTimeSeries]);

    const xResolution = useMemo(
        () => dailyAxisResolution || getXAxisResolution(data),
        [data, dailyAxisResolution]
    );

    const rawAdditionalTooltipValues =
        props.additionalTooltipValuesRef?.current ?? props.additionalTooltipValues;
    const sortedAndFilteredAdditionalTooltipValues = useMemo(() => {
        if (!rawAdditionalTooltipValues) return rawAdditionalTooltipValues;
        let filteredAdditionalTooltipValues = rawAdditionalTooltipValues;
        if (filteredIndices) {
            filteredAdditionalTooltipValues = filterAdditionalTooltipValues(
                rawAdditionalTooltipValues,
                filteredIndices
            );
        }

        if (!sortedIndices) return filteredAdditionalTooltipValues;
        return sortedIndices.map(i => filteredAdditionalTooltipValues[i]);
    }, [rawAdditionalTooltipValues, filteredIndices, sortedIndices]);

    const { seriesConfig, scaleConfig, onSeriesClick, legend: legendOriginal } = props;
    const getDataOnClick = useCallback(
        (seriesIndex: number | undefined, dataIdx: number | undefined, dataSet: DataSet): number =>
            seriesIndex !== undefined && dataIdx !== undefined
                ? (dataSet.columns[seriesIndex][dataIdx] ?? 0)
                : 0,
        []
    );
    const handleSelectedStateChange = useHandleSelectedStateChange(
        id,
        data,
        xResolution,
        setSyncedSelectedValue,
        setRangeSelection,
        onCrosshairClickFromProps,
        getDataOnClick
    );

    const handleHoverStateChange = useHandleHoverStateChange(
        id,
        data,
        hasProvider,
        setSyncedHoverValue,
        onCrosshairChangeFromProps
    );

    const crosshairValue = useMemo(
        () => (syncedHoverValue?.xVal ? { time: syncedHoverValue.xVal } : undefined),
        [syncedHoverValue]
    );
    const onCrosshairChange = handleHoverStateChange;
    const onCrosshairClick = useDynamicCallback(handleSelectedStateChange);
    const { cursorState } = useCursorPlugin({
        id,
        plotUtilitiesFn,
        plotRenderer,
        cursorEl,
        scaleFactor,
        crosshairValue,
        onCrosshairChange,
        onCrosshairClick,
        onRangeSelection,
    });

    // Event Annotations
    const [eventAnnotationCursorState, setEventAnnotationCursorState] = useState<EventAnnotation>();
    const onEventAnnotationClick = useDynamicCallback(props.eventsConfig?.onEventAnnotationClick);

    // Pin
    const handleClosePin = (): void => {
        setSyncedSelectedValue?.(undefined);
        props.onPinClose?.();
    };
    const onPinClose = useDynamicCallback(handleClosePin);

    const events = useMemo(
        () =>
            props.events?.map(event => ({
                ...event,
                time: normalizeTimeValue(event.time),
            })),
        [props.events]
    );

    const pinValue = useMemo((): PinValue | undefined => {
        const { xVal, seriesKey, originViz } = syncedSelectedValue || {};
        if (!xVal || !seriesKey || !timeWindow) {
            return undefined;
        }

        const [start, end] = timeWindow;
        const isWithinTimeWindow = start <= xVal && xVal <= end;

        return isWithinTimeWindow
            ? {
                  time: xVal,
                  isActive: originViz === id,
              }
            : undefined;
    }, [id, syncedSelectedValue, timeWindow]);

    // Series visibility
    const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
    const updateSeriesVisibility = useCallback(
        (field: string) => setHiddenSeries(currentlyHidden => xor(currentlyHidden, [field])),
        []
    );

    // Which field to focus (either a series or mark)
    const [selectedField, setSelectedField] = useState<string | null>(null);
    useEffect(() => {
        plotUtilities?.setFocusedSeries(selectedField);
        // We don't want to use the setFocusedSeries dep, it will cause excessive renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedField]);

    // Legend
    const { legend, legendSize, legendAnchor } = useMemo(() => {
        if (legendOriginal === false) {
            return { legend: null };
        }

        // eslint-disable-next-line no-underscore-dangle
        const _legend = exists(legendOriginal) ? legendOriginal : SideLegend;
        return {
            legend: React.createElement(_legend.component, {
                cursorState,
                plotUtilities,
                data,
                seriesConfig,
                scaleConfig,
                marks: props.marks,
                legendLabels: props.legendLabels,
                isSeriesCompare: props.isSeriesCompare,
                legendTruncation: props.legendTruncation,
                onSeriesClick: (field: string, ref: React.Ref<HTMLElement>) => {
                    if (props.legendClickTogglesSeriesVisibility) {
                        updateSeriesVisibility(field);
                    } else {
                        onSeriesClick?.(field, ref);
                    }
                },
                onSeriesHover: setSelectedField,
            }),
            legendSize: _legend.size,
            legendAnchor: _legend.anchor,
        };
    }, [
        legendOriginal,
        plotUtilities,
        cursorState,
        data,
        seriesConfig,
        scaleConfig,
        props.marks,
        props.legendLabels,
        props.isSeriesCompare,
        props.legendTruncation,
        onSeriesClick,
        props.legendClickTogglesSeriesVisibility,
        updateSeriesVisibility,
    ]);

    // Legend width tracking
    const { legendRef, actualLegendWidth } = useLegendWidth(legend, legendAnchor);

    // Calculate dimensions for plot renderer
    const { scaledPlotRendererWidth, scaledPlotRendererHeight } = useMemo(
        () =>
            calculatePlotDimensions({
                showTooltipOutsideChart: props.showTooltipOutsideChart ?? false,
                legend,
                legendAnchor,
                legendSize,
                width,
                height,
                scaleFactor,
                actualLegendWidth,
            }),
        [
            props.showTooltipOutsideChart,
            legend,
            legendAnchor,
            legendSize,
            width,
            height,
            scaleFactor,
            actualLegendWidth,
        ]
    );

    // Loading state
    if (props.loading || !data?.columns?.length) {
        return (
            <WaitSpinnerWrapper>
                <WaitSpinner />
            </WaitSpinnerWrapper>
        );
    }

    const isRowLayout = legend && (legendAnchor === 'right' || legendAnchor === 'left');
    const legendBefore = legend && (legendAnchor === 'left' || legendAnchor === 'top');
    const legendAfter = legend && (legendAnchor === 'right' || legendAnchor === 'bottom');

    return (
        <StyledTimeseriesChartContainer
            data-testid={id}
            data-test="timeseries-chart-root"
            ref={containerRef}
            className="TimeSeriesChart-root"
            height={containerHeight}
            width={containerWidth}
            style={
                isSingleVizFullscreen
                    ? {
                          height: '100vh',
                          width: '100vw',
                      }
                    : undefined
            }
        >
            {props.showTooltipOutsideChart && (
                <StyledTooltipOutsideChartWrapper data-test="tooltip-outside-chart-container">
                    <TooltipOutsideChart
                        plotUtilities={plotUtilities}
                        cursorState={cursorState}
                        data={data}
                        userLocale={localeInfo}
                        seriesConfig={props.seriesConfig}
                        scaleConfig={props.scaleConfig}
                        eventAnnotationCursorState={eventAnnotationCursorState}
                    />
                </StyledTooltipOutsideChartWrapper>
            )}
            <StyledTimeSeriesChart
                flexDirection={isRowLayout ? 'row' : 'column'}
                theme={theme}
                width={containerWidth}
            >
                {legendBefore ? (
                    <div
                        ref={legendAnchor === 'left' ? legendRef : null}
                        className="viz-legend"
                        data-test="viz-legend"
                    >
                        {legend}
                    </div>
                ) : null}
                {width > 0 && height > 0 ? (
                    <div style={{ position: 'relative', width: containerWidth }}>
                        <plotRenderer.component
                            width={scaledPlotRendererWidth}
                            height={scaledPlotRendererHeight}
                            data={data}
                            seriesConfig={props.seriesConfig}
                            scaleConfig={props.scaleConfig}
                            hideXAxis={props.hideXAxis}
                            interactiveEl={interactiveEl}
                            theme={theme}
                            range={props.range}
                            hiddenSeries={hiddenSeries}
                            userLocale={localeInfo}
                            maxSeriesFocus={props.maxSeriesFocus}
                            rawXaxisValues={rawXaxisValues}
                            timestampFormat={timestampFormat}
                            seriesColors={seriesColors}
                            seriesColorsByField={seriesColorsByField}
                            plugins={props.plugins}
                            showXMajorGridLines={props.showXMajorGridLines}
                            showYMajorGridLines={props.showYMajorGridLines}
                            showY2MajorGridLines={props.showY2MajorGridLines}
                            showYMinorGridLines={props.showYMinorGridLines}
                            showY2MinorGridLines={props.showY2MinorGridLines}
                            showLineSmoothing={props.showLineSmoothing}
                            stepPosition={props.stepPosition}
                            dataValuesDisplay={props.dataValuesDisplay}
                            showXMajorTicks={props.showXMajorTicks}
                            xAxisMajorTickSize={props.xAxisMajorTickSize}
                            showYMajorTicks={props.showYMajorTicks}
                            showY2MajorTicks={props.showY2MajorTicks}
                            yAxisMajorTickSize={props.yAxisMajorTickSize}
                            y2AxisMajorTickSize={props.y2AxisMajorTickSize}
                            yAxisMajorTickInterval={props.yAxisMajorTickInterval}
                            y2AxisMajorTickInterval={props.y2AxisMajorTickInterval}
                            showYMinorTicks={props.showYMinorTicks}
                            showY2MinorTicks={props.showY2MinorTicks}
                            yAxisMinorTickSize={props.yAxisMinorTickSize}
                            y2AxisMinorTickSize={props.y2AxisMinorTickSize}
                            showYAxisWithZero={props.showYAxisWithZero}
                            showY2AxisWithZero={props.showY2AxisWithZero}
                            showXAxisLine={props.showXAxisLine}
                            showYAxisLine={props.showYAxisLine}
                            showY2AxisLine={props.showY2AxisLine}
                            xAxisMaxLabelParts={props.xAxisMaxLabelParts}
                            areaOpacity={props.areaOpacity}
                        />
                    </div>
                ) : null}
                {legendAfter ? (
                    <div
                        ref={legendAnchor === 'right' ? legendRef : null}
                        className="viz-legend"
                        data-test="viz-legend"
                    >
                        {legend}
                    </div>
                ) : null}
                <ChartPortals
                    tooltipProps={{
                        tooltipEl,
                        cursorState,
                        plotUtilities,
                        tooltip: props.tooltip,
                        data,
                        seriesConfig: props.seriesConfig,
                        scaleConfig: props.scaleConfig,
                        eventAnnotationCursorState,
                        eventsConfig: props.eventsConfig,
                        chartTitle: props.title,
                        dailyAxisResolution,
                        localeInfo,
                        hideTooltipSeriesLabel,
                        showTooltipOutsideChart: props.showTooltipOutsideChart,
                        isTimeSeries,
                        rawXaxisValues,
                        additionalTooltipValues: sortedAndFilteredAdditionalTooltipValues,
                    }}
                    markProps={{
                        markEl,
                        plotUtilities,
                        cursorState,
                        marks: props.marks,
                        selectedField,
                    }}
                    rangeProps={{
                        rangeEl,
                        plotUtilities,
                        cursorState,
                        onRangeSelection: props.onRangeSelection,
                        rangeSelection,
                        id,
                        handleRangeSelectionChange,
                        onRangeZoom,
                        serverSideZoom: props.serverSideZoom,
                    }}
                    eventProps={{
                        eventAnnotationEl,
                        plotUtilities,
                        cursorState,
                        events,
                        eventsConfig: props.eventsConfig,
                        onEventAnnotationClick,
                        xResolution,
                        setEventAnnotationCursorState,
                    }}
                    pinProps={{
                        pinEl,
                        plotUtilities,
                        cursorState,
                        data,
                        pinValue,
                        onPinClose,
                    }}
                    alertBarProps={{
                        alertBarEl,
                        plotUtilities,
                        cursorState,
                        alerts: props.alerts,
                        alertsConfig: props.alertsConfig,
                        scaledPlotRendererWidth,
                    }}
                />
            </StyledTimeSeriesChart>
        </StyledTimeseriesChartContainer>
    );
};

export type TimeSeriesChartProps = InternalTimeSeriesChartProps & {
    theme?: VizThemeKey;
};

export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ theme, ...props }) => {
    // If user has not passed an explicit `theme` value,
    // we don't need to wrap in extra context provider (it will use default or existing context)
    if (!theme) {
        return <InternalTimeSeriesChart {...props} />;
    }

    return (
        <SplunkThemeProvider colorScheme={theme}>
            <InternalTimeSeriesChart {...props} />
        </SplunkThemeProvider>
    );
};
